import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { findRessourceBySlug } from "@/data/ressources";
import { META } from "@/config/seoMeta";
import { upsertMeta, upsertLink, setJsonLd, removeHeadNodes } from "@/lib/seoDom";

const SITE_URL = process.env.REACT_APP_SITE_URL || "https://horsindex.com";
const OG_HOME = `${SITE_URL}/og-home.jpg`;

// Même convention que scripts/generate-og.mjs : "/" → og-home.jpg,
// sinon /og/{path-avec-tirets}.jpg (miniature réelle de la page).
function ogImageFor(path, isRealPage) {
  if (!isRealPage || path === "/") return OG_HOME;
  return `${SITE_URL}/og/${path.slice(1).replace(/\//g, "-")}.jpg`;
}

const NOT_FOUND_META = {
  title: "Page introuvable | HorsIndex",
  description:
    "La page demandée n'existe pas, n'est plus disponible ou n'est pas accessible publiquement.",
  robots: "noindex, nofollow, noarchive",
};

// Dynamic resolution for individual resource pages: /comprendre/:slug
// (or legacy /ressources/:slug which 301-redirects but may briefly render)
function resolveResourceMeta(path) {
  if (!path.startsWith("/comprendre/") && !path.startsWith("/ressources/")) return null;
  const prefix = path.startsWith("/comprendre/") ? "/comprendre/" : "/ressources/";
  const slug = path.slice(prefix.length).replace(/\/$/, "");
  const article = findRessourceBySlug(slug);
  if (!article) return null;
  return {
    title: `${article.seoTitle || article.titre} | HorsIndex`,
    description: article.seoDescription || article.resume,
    article,
  };
}

function applyMetaTags(routeMeta, fullUrl, isArticle, ogImage, imageAlt) {
  document.title = routeMeta.title;
  document.documentElement.lang = "fr";

  upsertMeta("name", "description", "content", routeMeta.description);
  upsertMeta("name", "author", "content", "HorsIndex");
  upsertMeta("name", "robots", "content", routeMeta.robots || "index,follow");
  upsertMeta("name", "theme-color", "content", "#FAF9F7");

  upsertMeta("property", "og:type", "content", isArticle ? "article" : "website");
  upsertMeta("property", "og:site_name", "content", "HorsIndex");
  upsertMeta("property", "og:title", "content", routeMeta.title);
  upsertMeta("property", "og:description", "content", routeMeta.description);
  upsertMeta("property", "og:url", "content", fullUrl);
  upsertMeta("property", "og:locale", "content", "fr_FR");
  upsertMeta("property", "og:image", "content", ogImage);
  upsertMeta("property", "og:image:secure_url", "content", ogImage);
  upsertMeta("property", "og:image:type", "content", "image/jpeg");
  upsertMeta("property", "og:image:width", "content", "1680");
  upsertMeta("property", "og:image:height", "content", "882");
  upsertMeta("property", "og:image:alt", "content", imageAlt);

  removeHeadNodes('meta[property="og:locale:alternate"]');

  upsertMeta("name", "twitter:card", "content", "summary_large_image");
  upsertMeta("name", "twitter:title", "content", routeMeta.title);
  upsertMeta("name", "twitter:description", "content", routeMeta.description);
  upsertMeta("name", "twitter:image", "content", ogImage);
  upsertMeta("name", "twitter:image:alt", "content", imageAlt);
}

// Article + BreadcrumbList structured data, injected on article pages only.
function applyStructuredData(resourceMeta, fullUrl, ogImage) {
  setJsonLd(
    "seo-jsonld-article",
    resourceMeta && {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: resourceMeta.article.titre,
      description: resourceMeta.description,
      inLanguage: "fr",
      mainEntityOfPage: fullUrl,
      image: ogImage,
      author: { "@type": "Organization", name: "HorsIndex" },
      publisher: { "@type": "Organization", name: "HorsIndex", url: SITE_URL },
    }
  );
  setJsonLd(
    "seo-jsonld-breadcrumb",
    resourceMeta && {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Comprendre", item: `${SITE_URL}/comprendre/` },
        { "@type": "ListItem", position: 3, name: resourceMeta.article.titre },
      ],
    }
  );
}

export default function SEOHead() {
  const location = useLocation();

  useEffect(() => {
    // Normalise le slash final : Netlify sert les pages sous /page/
    // alors que la table des routes est indexée sans slash.
    const path = (location.pathname || "/").replace(/\/+$/, "") || "/";
    const langDict = META.fr;
    const knownPath = Object.prototype.hasOwnProperty.call(langDict.routes, path);
    const resourceMeta = knownPath ? null : resolveResourceMeta(path);
    const routeMeta = knownPath
      ? langDict.routes[path]
      : resourceMeta || NOT_FOUND_META;
    const fullUrl = `${SITE_URL}${path === "/" ? "/" : path + "/"}`;
    const isRealPage = knownPath || Boolean(resourceMeta);
    const ogImage = ogImageFor(path, isRealPage);
    const imageAlt =
      path === "/"
        ? langDict.imageAlt
        : `Aperçu de la page ${routeMeta.title.split("|")[0].trim()} sur HorsIndex.`;

    applyMetaTags(routeMeta, fullUrl, Boolean(resourceMeta), ogImage, imageAlt);
    applyStructuredData(resourceMeta, fullUrl, ogImage);

    // 404: no self-canonical, no og:url, and never the home canonical.
    const is404 = !knownPath && !resourceMeta;
    if (is404) {
      removeHeadNodes('link[rel="canonical"]');
      removeHeadNodes('meta[property="og:url"]');
    } else {
      upsertLink("canonical", fullUrl);
    }
    // FR-only site — strip any hreflang alternates left in the DOM.
    removeHeadNodes('link[rel="alternate"][hreflang]');
  }, [location.pathname]);

  return null;
}
